// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { observable, computed, action, makeObservable, runInAction } from 'mobx';

import Deliverable,
{
  DeliverableDTO,
  DeliverableTypeDTO,
  DeliverableDownloadAccessDTO
} from '@shared/models/deliverable';

import { DimensionDTO } from "@shared/models/deliverable/dimension";

import container from '@core/di';
import DeliverablesService from '@shared/services/deliverables';
import BaseStore from '@core/stores/base';
import CommentsStore from './comments';
import { Id } from '@shared/types/common';
import { ListRequestParams, PageInfo } from '@shared/types/services';
import Projection from '@shared/models/deliverable/projection';
import Datum from '@shared/models/deliverable/datum';
import { TaskTreeItemDTO } from '@shared/models/deliverable/task-tree-item';
import { downloadFileNoType } from '@shared/utils/file';
import { SDownloadInfo } from '@shared/models/sp-sheets';

@injectable()
export default class DeliverablesStore extends BaseStore<DeliverableDTO, Deliverable, DeliverablesService> {
  static diToken = Symbol('deliverables-store');
  commentsStore = new CommentsStore({ urlPrefix: 'deliverable' });

  // Vars to store data from the deliverable tables.  Note: @observable necessary here ???
  @observable _deliverableTypes: Array<DeliverableTypeDTO> = [];
  @observable _deliverableProjections: Array<Projection> = [];
  @observable _deliverableProjectionsPageInfo;
  @observable _deliverableDatums: Array<Datum> = [];
  @observable _deliverableDatumsPageInfo;
  @observable _deliverableDimensions: Array<DimensionDTO> = [];
  @observable _deliverableTaskTree: Array<TaskTreeItemDTO> = [];

  constructor() {
    super({ service: container.get<DeliverablesService>(DeliverablesService.diToken) });

    this.loading = {
      types: false,
      projections: false,
      datums: false,
      dimensions: false,
      item: false,
    };
    this._deliverableProjectionsPageInfo = undefined;
    this._deliverableDatumsPageInfo = undefined;

    makeObservable(this);
  }

  // "get" methods to obtain item lists (rows) from deliverable DB tables.
  @computed get deliverableTypes() {
    return this._deliverableTypes;
  }

  @computed get deliverableProjections() {
    return this._deliverableProjections;
  }

  @computed get deliverableProjectionsPageInfo() {
    return this._deliverableProjectionsPageInfo;
  }

  @computed get deliverableDatums() {
    return this._deliverableDatums;
  }

  @computed get deliverableDimensions() {
    return this._deliverableDimensions;
  }

  @computed get deliverableTaskTree() {
    return this._deliverableTaskTree;
  }


  @computed get deliverableDatumsPageInfo() {
    return this._deliverableDatumsPageInfo;
  }

  //
  // Helper methods to load records/rows from deliverable DB tables to local vars.
  //

  // deliverable_type table.
  @action getDeliverableTypes = async () => {
    runInAction(() => {
      this.loading.types = true;
    });

    // Use existing result if available.  Values are not likely to change.  
    if(this._deliverableTypes.length > 0) {
      runInAction(() => {
        this.loading.types = false;
      });
      return this._deliverableTypes;
    }

    // Load from portal-api.
    try {
      const deliverableTypes = await this.service.getDeliverableTypes();
      runInAction(() => {
        this._deliverableTypes = deliverableTypes;
        this.loading.types = false;
      });
      return this._deliverableTypes;
    } 
    catch {
      runInAction(() => {
        this.loading.types = false;
      });
    }
  }

  // deliverable_dimension table.
  @action getDeliverableDimensions = async () => {
    runInAction(() => {
      this.loading.dimensions = true;
    });

    // Use existing result.  Values are not likely to change.  
    if(this._deliverableDimensions.length > 0) {
      runInAction(() => {
        this.loading.dimensionss = false;
      });
      return this._deliverableDimensions;
    }

    try {
      this._deliverableDimensions = await this.service.getDeliverableDimensions();
      runInAction(() => {
        this.loading.dimensions = false;
      });
      return this._deliverableDimensions;
    } catch {
      runInAction(() => {
        this.loading.dimensionss = false;
      });
    }
  }

  // Convert dimesion rows to lookup table with key is names and value is Ids.
  @action getDimensionName2Id = async () => {
    const dimensionName2Id: Map<string, number> = new Map<string, number>();

    // Use existing result.  Values are not likely to change.  
    if(this._deliverableDimensions.length == 0) 
    {
      try 
      {
        this._deliverableDimensions = await this.service.getDeliverableDimensions();
      } 
      catch(exc) 
      {
        console.log("Exception in getDimesionName2Id: ", exc);
        runInAction(() => {
          this.loading.dimensionss = false;
        });
      }
    }

    // Map name to id.
    this._deliverableDimensions.forEach((d) => dimensionName2Id.set(d.name, d.id));
    const tmpId = dimensionName2Id.get("LiDAR");
    dimensionName2Id.set("Ground", tmpId === undefined ? 100000 : tmpId); // Kludge to handle "Ground" which is "LiDAR".

    return dimensionName2Id;
  }

  // Convert dimesion rows to lookup table with key is Id and value is Name.
  @action getDimensionId2Name = async () => {
    const dimensionId2Name: Map<number, string> = new Map<number, string>();

    // Use existing result.  Values are not likely to change.  
    if(this._deliverableDimensions.length == 0) 
    {
      try 
      {
        this._deliverableDimensions = await this.service.getDeliverableDimensions();
      } 
      catch(exc) 
      {
        console.log("Exception in getDimensionId2Name: ", exc);
        runInAction(() => {
          this.loading.dimensionss = false;
        });
      }
    }

    // Map id to name.
    this._deliverableDimensions.forEach((d) => d.name === "LiDAR" ? dimensionId2Name.set(d.id, "Ground") : dimensionId2Name.set(d.id, d.name));
    dimensionId2Name.set(0, ""); 

    return dimensionId2Name;
  }

  // Convert dimesion rows to lookup table with key is Id and value is Name.
  @action getField2DimensionId = async () => {
    const field2Id: Map<string, number> = new Map<string, number>();

    // Use existing result.  Values are not likely to change.  
    if(this._deliverableDimensions.length == 0) 
    {
      try 
      {
        this._deliverableDimensions = await this.service.getDeliverableDimensions();
      } 
      catch(exc) 
      {
        console.log("Exception in getDimesionName2Id: ", exc);
        runInAction(() => {
          this.loading.dimensionss = false;
        });
      }
    }

    // Map name to id.
    this._deliverableDimensions.forEach((d) => field2Id.set(d.name, d.id));
    // Additional fields.
    field2Id.set('information', 0);
    field2Id.set('team', 0);
    field2Id.set('folders', 0);
    field2Id.set('shipping', 0);
    field2Id.set('JobRequests', 0);
    //field2Id.set('flightCollection', 1);
    //field2Id.set('flightCollections', 1);
    field2Id.set('deliveries', 6);

    return field2Id;
  }
  

  // Task tree from portal-api.
  // initCode = 2: for editing, uncheck all workflows.  Otherwise, check the first (default) workflow.
  @action 
  async getTaskTree(initCode: number = 0): Promise<Array<TaskTreeItemDTO>> {
    runInAction(() => {
      this.loading.taskTree = true;
    });

    try {
      const data = await this.service.getTaskTree(initCode);
      runInAction(() => {
        this._deliverableTaskTree = data;
        this.loading.taskTree = false;
      });

      return this._deliverableTaskTree;
    } 
    catch {
      runInAction(() => {
        this.loading.taskTree = false;
      });

      return [];
    }
  }

  // deliverable_projections table.  WXYZ: function does not return a value.
  @action getDeliverableProjections = async (params: ListRequestParams) => {
    runInAction(() => {
      this.loading.projections = true;
    });

    try {
      const { items, pageInfo } = await this.service.getDeliverableProjections(params);

      runInAction(() => {
        this._deliverableProjections = items;
        this._deliverableProjectionsPageInfo = pageInfo;
        this.loading.projections = false;
      });
    } catch {
      runInAction(() => {
        this.loading.projections = false;
      });
    }
  }

  // deliverable_datums table.  WXYZ: function does not return a value.
  @action getDeliverableDatums = async (params: ListRequestParams) => {
    runInAction(() => {
      this.loading.datums = true;
    });

    try {
      const { pageInfo, items } = await this.service.getDeliverableDatums(params);

      runInAction(() => {
        this._deliverableDatums = items;
        this._deliverableDatumsPageInfo = pageInfo;

        this.loading.datums = false;
      });
    } catch {
      runInAction(() => {
        this.loading.datums = false;
      });
    }
  }

  getDownloadInstruction = (deliverableId: DeliverableDTO['id']) => {
    return this.service.getDownloadInstruction(deliverableId);
  }

  // Get methods to update the deliverable DB tables.
  edit = (id: Id, data: Partial<DeliverableDTO>) => {
    return this.service.edit(id, data);
  }

  updateDownloadInstruction = (deliverableId: DeliverableDTO['id'], data: DeliverableDownloadAccessDTO) => {
    return this.service.updateDownloadInstruction(deliverableId, data);
  }

  editDatumProjection(
    deliverableId: Id, 
    data: { projectionId?: Id, datumId?: Id }
  ): Promise<void> {
    return this.service.editDatumProjection(deliverableId, data);
  }
  
  @action downloadACS = async (deliverableId: string, filename: string) => {
    const { data, headers } = await this.service.downloadACS(deliverableId);

    if (data) {
      if (headers && headers['content-type'] === 'application/zip')
      {
        filename = filename.concat('.zip');
      }
      else
      {
        filename = filename.concat('.acs');
      }
      downloadFileNoType([data], filename);
    }
  }

  @action downloadOrbitQCPdf = async (deliverableId: string, filename: string) => {
    const { data, headers } = await this.service.downloadOrbitQCPdf(deliverableId);

    if (data) {
      if (headers && headers['content-type'] === 'application/zip')
      {
        filename = filename.concat('.zip');
      }
      else
      {
        filename = filename.concat('.pdf');
      }
      downloadFileNoType([data], filename);
    }
  }

  // Download multiple collection data files.  
  // 'downloadInfo' holds the site name and type of data (ACS, PDF, ...) to download.
  @action downloadMultiples = async (downloadInfo: Array<SDownloadInfo>) => {
    // Forward to service method.
    const { data, headers } = await this.service.downloadMultiples(downloadInfo);
    const filename = 'VIPDownload.zip'
    if (data) {
      if (headers && headers['content-type'] === 'application/zip')
      {
        downloadFileNoType([data], filename);
      }
    }
  }
}